/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from 'axios';
import {
  isAccessTokenExpiredResponseError,
  refreshAccessToken,
} from './RefreshTokenClient';

export interface IConfig {
  baseURL?: string;
  headers?: any;
  notFoundAsNull?: boolean;
}

export interface IRequest extends AxiosRequestConfig {
  url?: string;
  method?: Method;
  headers?: any;
  params?: Params;
  data?: any;
}

export interface IRestClient {
  request<ResData>(request: IRequest): Promise<ResData>;
  get<ResData>(url: string, request: IRequest): Promise<ResData>;
  post<ResData>(url: string, request: IRequest): Promise<ResData>;
  put<ResData>(url: string, request: IRequest): Promise<ResData>;
  delete<ResData>(url: string, request: IRequest): Promise<ResData>;
}

export type Params = { [key: string]: string | number | boolean };

type RetriableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

const SERVER_SESSION_FIELDS = ['userId', 'potentialLeadId'];

function removeServerSessionFields(value: any) {
  if (!value) return value;

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    SERVER_SESSION_FIELDS.forEach(field => value.delete(field));
    return value;
  }

  if (typeof value !== 'object' || Array.isArray(value)) return value;

  return Object.keys(value).reduce((result, key) => {
    if (SERVER_SESSION_FIELDS.includes(key)) return result;
    return { ...result, [key]: value[key] };
  }, {} as any);
}

export class ResponseError extends Error {
  status?: number;
  statusText?: string;
  data?: any;
  headers?: any;
  request?: AxiosRequestConfig;

  constructor(
    request: AxiosRequestConfig,
    status: number,
    statusText?: string,
    headers?: any,
    data?: any,
  ) {
    super(`${status} ${statusText}`);
    this.status = status;
    this.statusText = statusText;
    this.headers = headers;
    this.data = data;
    this.request = request;
  }
}

export class RequestError extends Error {
  message: string;
  request: AxiosRequestConfig;

  constructor(request: AxiosRequestConfig, message: string) {
    super(message);
    this.message = message;
    this.request = request;
  }
}

function handleError(error: any, config: IConfig = {}) {
  if (error.response) {
    if (error.response.status === 404 && config.notFoundAsNull === true) {
      return Promise.reject(null);
    }

    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const { status, statusText, headers, data } = error.response;

    return Promise.reject(
      new ResponseError(config, status, statusText, headers, data),
    );
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    const { message } = error;
    return Promise.reject(new RequestError(error.config, message));
  }

  return Promise.reject(error);
}

function handleResponse<ResData>(response: AxiosResponse<ResData>) {
  return response.data;
}

export class RestClient implements IRestClient {
  private axios: AxiosInstance;

  constructor(private config?: IConfig) {
    this.axios = axios.create({
      ...config,
      withCredentials: true,
    });
    this.config = config;

    this.axios.interceptors.request.use(req => {
      req.headers = req.headers ?? {};
      delete req.headers['Authorization'];
      delete req.headers['authorization'];
      return req;
    });
  }

  request<ResData>(request: IRequest): Promise<ResData> {
    const config: RetriableRequestConfig = {
      ...request,
      params: removeServerSessionFields(request.params),
      data: removeServerSessionFields(request.data),
      timeout: 30000,
      withCredentials: request.withCredentials ?? true,
    };

    return this.axios
      .request<ResData>(config)
      .then((response: AxiosResponse<ResData>) => handleResponse(response))
      .catch((error: any) => {
        if (!isAccessTokenExpiredResponseError(error) || config._retry) {
          return Promise.reject(error);
        }

        config._retry = true;
        return refreshAccessToken(this.config?.baseURL).then(() =>
          this.axios
            .request<ResData>(config)
            .then((response: AxiosResponse<ResData>) =>
              handleResponse(response),
            ),
        );
      })
      .catch((error: any) => handleError(error, this.config));
  }

  get<ResData>(url: string, request: IRequest = {}) {
    return this.request<ResData>({
      method: 'GET',
      url,
      ...request,
    });
  }

  post<ResData>(url: string, request: IRequest = {}) {
    return this.request<ResData>({
      method: 'POST',
      url,
      ...request,
    });
  }

  put<ResData>(url: string, request: IRequest = {}) {
    return this.request<ResData>({
      method: 'PUT',
      url,
      ...request,
    });
  }

  delete<ResData>(url: string, request: IRequest = {}) {
    return this.request<ResData>({
      method: 'DELETE',
      url,
      ...request,
    });
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
// import axios, {
//   AxiosInstance,
//   AxiosRequestConfig,
//   AxiosResponse,
//   Method,
// } from 'axios';

// export interface IConfig {
//   baseURL?: string;
//   headers?: any;
//   notFoundAsNull?: boolean;
//   onUnauthorized?: () => void; // <- callback เวลา 401
// }

// export interface IRequest extends AxiosRequestConfig {
//   url?: string;
//   method?: Method;
//   headers?: any;
//   params?: Params;
//   data?: any;
// }

// export interface IRestClient {
//   request<ResData>(request: IRequest): Promise<ResData>;
//   get<ResData>(url: string, request?: IRequest): Promise<ResData>;
//   post<ResData>(url: string, request?: IRequest): Promise<ResData>;
//   put<ResData>(url: string, request?: IRequest): Promise<ResData>;
//   delete<ResData>(url: string, request?: IRequest): Promise<ResData>;
// }

// export type Params = { [key: string]: string | number | boolean };

// export class ResponseError extends Error {
//   status?: number;
//   statusText?: string;
//   data?: any;
//   headers?: any;
//   request?: AxiosRequestConfig;

//   constructor(
//     request: AxiosRequestConfig,
//     status: number,
//     statusText?: string,
//     headers?: any,
//     data?: any,
//   ) {
//     super(`${status} ${statusText}`);
//     this.status = status;
//     this.statusText = statusText;
//     this.headers = headers;
//     this.data = data;
//     this.request = request;
//   }
// }

// export class RequestError extends Error {
//   message: string;
//   request: AxiosRequestConfig;

//   constructor(request: AxiosRequestConfig, message: string) {
//     super(message);
//     this.message = message;
//     this.request = request;
//   }
// }

// function withBearer(t?: string | null) {
//   if (!t) return '';
//   return t.startsWith('Bearer ') ? t : `Bearer ${t}`;
// }

// function handleError(error: any, config: IConfig = {}) {
//   if (error.response) {
//     if (error.response.status === 404 && config.notFoundAsNull === true) {
//       return Promise.reject(null);
//     }
//     const {
//       status,
//       statusText,
//       headers,
//       data,
//       config: reqConfig,
//     } = error.response;
//     // ✅ เดิมส่ง IConfig เข้าไปผิดตัว เปลี่ยนเป็น reqConfig
//     return Promise.reject(
//       new ResponseError(reqConfig, status, statusText, headers, data),
//     );
//   } else if (error.request) {
//     const { message } = error;
//     return Promise.reject(new RequestError(error.config, message));
//   }
//   return Promise.reject(error);
// }

// function handleResponse<ResData>(response: AxiosResponse<ResData>) {
//   return response.data;
// }

// export class RestClient implements IRestClient {
//   private axios: AxiosInstance;

//   constructor(private config: IConfig = {}) {
//     this.axios = axios.create({
//       baseURL: config.baseURL,
//       headers: config.headers,
//     });

//     // -------- Request Interceptor: เติม Authorization ให้อัตโนมัติ --------
//     this.axios.interceptors.request.use(req => {
//       // อย่าทับ header ที่ผู้เรียกกำหนดเองมาแล้ว
//       req.headers = req.headers ?? {};

//       // ถ้ายังไม่มี Authorization ให้เติมจาก localStorage
//       if (!req.headers['Authorization'] && !req.headers['authorization']) {
//         const token = localStorage.getItem('driver-portal-access-token');
//         if (token) {
//           req.headers['Authorization'] = withBearer(token);
//         }
//       }
//       return req;
//     });

//     // -------- Response Interceptor: จัดการ 401 ให้ออกสู่ login --------
//     this.axios.interceptors.response.use(
//       res => res,
//       async err => {
//         const status = err?.response?.status;
//         if (status === 401) {
//           try {
//             // ล้าง token พื้นฐาน
//             localStorage.removeItem('driver-portal-access-token');
//             localStorage.removeItem('driver-portal-refresh-token');
//             localStorage.removeItem('driver-portal-user-id');
//             localStorage.removeItem('driver-portal-potential-lead-id');
//           } finally {
//             if (typeof this.config.onUnauthorized === 'function') {
//               this.config.onUnauthorized();
//             } else if (
//               typeof window !== 'undefined' &&
//               window.location.pathname !== '/login'
//             ) {
//               window.location.href = '/login';
//             }
//           }
//         }
//         // ส่งต่อ error ปกติ (ให้ไปเข้าที่ handleError)
//         return Promise.reject(err);
//       },
//     );
//   }

//   request<ResData>(request: IRequest): Promise<ResData> {
//     const config: AxiosRequestConfig = {
//       ...request,
//       timeout: 30000,
//     };
//     return this.axios
//       .request<ResData>(config)
//       .then(response => handleResponse(response))
//       .catch(error => handleError(error, this.config));
//   }

//   get<ResData>(url: string, request: IRequest = {}) {
//     return this.request<ResData>({ method: 'GET', url, ...request });
//   }
//   post<ResData>(url: string, request: IRequest = {}) {
//     return this.request<ResData>({ method: 'POST', url, ...request });
//   }
//   put<ResData>(url: string, request: IRequest = {}) {
//     return this.request<ResData>({ method: 'PUT', url, ...request });
//   }
//   delete<ResData>(url: string, request: IRequest = {}) {
//     return this.request<ResData>({ method: 'DELETE', url, ...request });
//   }
// }
